import { IListenerConfig } from '../../../framework/BaseWebComponentController';
import { EasmArtifactsInventoryGridView } from '../view/EasmArtifactsInventoryGridView';
import { EasmArtifactsInventoryGridWebComponent } from '../component/EasmArtifactsInventoryGridWebComponent';
import { EasmArtifactsInventoryRepository } from '../repository/EasmArtifactsInventoryRepository';
import easmArtifactsInventoryGridTemplate from '../view/template';
import { SmartViewEvents } from '../../../grid/genericGridSmartViews/types';
import { GridEvents } from '../../../grid/types/common';
import { ButtonEvents } from '../../../ui/gzButton/types';
import { EASMSidePanelEvents } from '../../findingDetails/findingDetails/types';
import { FilterChangeEvents } from '../../../grid/gridFilters/types';
import { EasmInventoryBaseGridController } from '../../assetsInventoryGrid/controller/EasmInventoryBaseGridController';
import { Events, FindingClassType } from '../../types';
import { ColumnKeys } from '../types';
import { SideMenuEvents } from '../../../ui/gzSideMenu/types';
import { Events as BulkChangePropertyEvents, Property } from '../../bulkChangeProperty/types';
import { GzSelectMenuWithButtonEvents } from '../../../ui/gzSelectMenuWithButton/types';

export class EasmArtifactsInventoryGridController extends EasmInventoryBaseGridController {
  public component: EasmArtifactsInventoryGridWebComponent;
  public dataRepository: EasmArtifactsInventoryRepository;
  public view: EasmArtifactsInventoryGridView;

  public constructor(component: EasmArtifactsInventoryGridWebComponent) {
    super(component);

    this.initialSetup({
      dataRepositoryConstructor: EasmArtifactsInventoryRepository,
      viewConstructor: EasmArtifactsInventoryGridView,
      viewTemplate: easmArtifactsInventoryGridTemplate,
    });
  }

  public getCompanyIdFilterValue(): string {
    return this.view.getCompanyIdFilterValue();
  }

  public computeEventListeners(): IListenerConfig[] {
    this.view.dom.$gridView.onDataLoadingFinishedWithSuccess = this.onDataLoadingFinishedWithSuccess.bind(this);
    return [
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.onSmartViewSelect,
      },
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_AFTER_CREATE,
        callback: this.onSmartViewCreated,
      },
      {
        element: this.view.dom.$gridView,
        event: SmartViewEvents.SMART_VIEW_SAVED,
        callback: this.updateViewProps,
      },
      {
        element: this.view.dom.$gridView,
        event: SmartViewEvents.SHOW_SAVE_VIEW_AS_DIALOG,
        callback: this.showSaveAsDialog,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.SELECT_ROWS,
        callback: this.handleSelectionChange,
      },
      {
        element: this.view.dom.$gridView,
        event: SmartViewEvents.SMART_VIEW_FAVORITE_CHANGED,
        callback: this.handleSmartViewFavoriteChanged,
      },
      {
        element: this.view.dom.$scanConfigurationBtn,
        event: ButtonEvents.CLICK,
        callback: this.openScanConfigurationWithResults,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClick.bind(this),
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.EXPORT_CSV,
        callback: this.handleCSVExportEvent,
      },
      {
        element: this.view.dom.$detailsPanel,
        event: EASMSidePanelEvents.SHOW_NEW_ASSET,
        callback: this.showNewAssetSidePanel,
      },
      {
        element: this.view.dom.$detailsPanel,
        event: SideMenuEvents.CLOSE,
        callback: this.handleDetailsPanelClose,
      },
      {
        element: this.view.dom.$changeInvestigationStatusBtn,
        event: GzSelectMenuWithButtonEvents.CHANGE,
        callback: (event: CustomEvent): void => {
          this.handleBulkChangePropertyButtonClick(Property.INVESTIGATION_STATUS, event);
        },
      },
      {
        element: this.view.dom.$assignBtn,
        event: GzSelectMenuWithButtonEvents.CHANGE,
        callback: (event: CustomEvent): void => {
          this.handleBulkChangePropertyButtonClick(Property.ASSIGNEE, event);
        },
      },
      {
        element: this.view.dom.$prioritizeBtn,
        event: GzSelectMenuWithButtonEvents.CHANGE,
        callback: (event: CustomEvent): void => {
          this.handleBulkChangePropertyButtonClick(Property.PRIORITY, event);
        },
      },
      {
        element: this.view.dom.$gridView,
        event: FilterChangeEvents.REMOTE_SELECT_CHANGED,
        callback: this.handleCompanyFilterUpdated,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.GRID_LOADED,
        callback: this.openDetailsPanelIfDemanded,
      },
      {
        element: this.view.dom.$gridView,
        event: Events.CHANGE_PROPERTY,
        callback: this.handleChangeProperty,
      },
      {
        element: document.body,
        event: BulkChangePropertyEvents.PROPERTY_CHANGED,
        callback: (): Promise<void> => this.refreshGrid(true),
      },
    ];
  }

  protected complexEnumFilterColumns(): string[] {
    return [ColumnKeys.NAME, ColumnKeys.ASSETS_RELATED_TO_ARTIFACT];
  }

  protected findingClassType(): FindingClassType {
    return FindingClassType.ARTIFACT;
  }

  private handleSelectionChange(event: CustomEvent): void {
    this.selectedItemIds = event.detail.selectionStoreData;

    const noItemsSelected = 0 === this.selectedItemIds.length;

    this.view.dom.$changeInvestigationStatusBtn.disabled = noItemsSelected;
    this.view.dom.$assignBtn.disabled = noItemsSelected;
    this.view.dom.$prioritizeBtn.disabled = noItemsSelected;
  }

  private async handleDetailsPanelClose(): Promise<void> {
    if (this.view.dom.$detailsPanel.shouldGridBeRefreshed) {
      await this.refreshGrid(false);
    }
  }
}
